import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { Header } from '@/components/layout/Header';
import { StatusBadge, ChannelBadge, RiskBadge, JobTypeBadge } from '@/components/common/StatusBadge';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useApi } from '@/hooks/useApi';
import { toast } from 'sonner';
import {
  ChevronRight,
  Filter,
  MessageSquareText,
  Calendar,
  Building2,
  AlertTriangle
} from 'lucide-react';

export const ReviewJobsListPage = ({ jobType = 'guest_review' }) => {
  const api = useApi();
  const [searchParams, setSearchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const statusFilter = searchParams.get('status') || '';
  const channelFilter = searchParams.get('channel') || '';
  const riskFilter = searchParams.get('risk') || '';

  const fetchJobs = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append('job_type', jobType);
      if (statusFilter) params.append('status', statusFilter);
      if (channelFilter) params.append('channel', channelFilter);
      if (riskFilter) params.append('risk_level', riskFilter);

      const response = await api.get(`/review-jobs?${params.toString()}`);
      setJobs(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [api, jobType, statusFilter, channelFilter, riskFilter]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchJobs();
  };

  const updateFilter = (key, value) => {
    if (value && value !== 'all') {
      searchParams.set(key, value);
    } else {
      searchParams.delete(key);
    }
    setSearchParams(searchParams);
  };

  const title = jobType === 'guest_review' ? 'Avis voyageurs' : 'Réponses aux avis';
  const subtitle = jobType === 'guest_review'
    ? 'Générez et validez les avis pour vos voyageurs'
    : 'Répondez aux commentaires laissés par vos voyageurs';

  return (
    <MainLayout>
      <Header
        title={title}
        subtitle={subtitle}
        onRefresh={handleRefresh}
        refreshing={refreshing}
      />

      <div className="p-6 space-y-6 animate-fadeIn" data-testid="review-jobs-list">
        {/* Filters */}
        <Card className="bg-white border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2 text-slate-500">
                <Filter size={16} />
                <span className="text-sm font-medium">Filtres</span>
              </div>

              <Select value={statusFilter || 'all'} onValueChange={(v) => updateFilter('status', v)}>
                <SelectTrigger className="w-[180px]" data-testid="filter-status">
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="new">Nouveau</SelectItem>
                  <SelectItem value="draft_ready">Brouillon prêt</SelectItem>
                  <SelectItem value="needs_human_review">Revue requise</SelectItem>
                  <SelectItem value="approved">Approuvé</SelectItem>
                  <SelectItem value="manual_publish_required">À publier</SelectItem>
                  <SelectItem value="published">Publié</SelectItem>
                  <SelectItem value="rejected">Rejeté</SelectItem>
                </SelectContent>
              </Select>

              <Select value={channelFilter || 'all'} onValueChange={(v) => updateFilter('channel', v)}>
                <SelectTrigger className="w-[150px]" data-testid="filter-channel">
                  <SelectValue placeholder="Canal" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les canaux</SelectItem>
                  <SelectItem value="airbnb">Airbnb</SelectItem>
                  <SelectItem value="booking">Booking.com</SelectItem>
                  <SelectItem value="direct">Direct</SelectItem>
                </SelectContent>
              </Select>

              <Select value={riskFilter || 'all'} onValueChange={(v) => updateFilter('risk', v)}>
                <SelectTrigger className="w-[140px]" data-testid="filter-risk">
                  <SelectValue placeholder="Risque" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tout niveau</SelectItem>
                  <SelectItem value="low">Faible</SelectItem>
                  <SelectItem value="medium">Moyen</SelectItem>
                  <SelectItem value="high">Élevé</SelectItem>
                </SelectContent>
              </Select>

              <div className="flex-1" />

              <span className="text-sm text-slate-500">
                {jobs.length} résultat{jobs.length > 1 ? 's' : ''}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Jobs list */}
        {loading ? (
          <div className="text-center py-12 text-slate-400">Chargement...</div>
        ) : jobs.length === 0 ? (
          <Card className="bg-white border-slate-100">
            <CardContent className="py-12 text-center">
              <MessageSquareText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Aucun {jobType === 'guest_review' ? 'avis' : 'réponse'} à afficher</p>
              <p className="text-sm text-slate-400 mt-1">
                Les jobs apparaîtront après synchronisation avec Beds24
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {jobs.map((job) => (
              <Link
                key={job.id}
                to={`/review/${job.id}`}
                className="block"
                data-testid={`job-card-${job.id}`}
              >
                <Card className={`bg-white border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-all cursor-pointer ${
                    job.risk_level !== 'low' ? 'border-l-4 border-l-red-400' : ''
                  }`}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        job.risk_level !== 'low' ? 'bg-red-100' :
                        job.job_type === 'guest_review' ? 'bg-violet-100' : 'bg-cyan-100'
                      }`}>
                        {job.risk_level !== 'low' ? (
                          <AlertTriangle className="h-6 w-6 text-red-500" />
                        ) : (
                          <MessageSquareText className={`h-6 w-6 ${
                            job.job_type === 'guest_review' ? 'text-violet-600' : 'text-cyan-600'
                          }`} />
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-slate-900 truncate">{job.guest_name}</h3>
                          <JobTypeBadge type={job.job_type} />
                          {job.risk_level !== 'low' && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 text-red-700" data-testid={`custom-badge-${job.id}`}>
                              <AlertTriangle size={10} />
                              Sur mesure
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <Building2 size={14} />
                            {job.property_name}
                          </span>
                          {job.check_out && !isNaN(new Date(job.check_out).getTime()) && (
                            <span className="flex items-center gap-1">
                              <Calendar size={14} />
                              {new Date(job.check_out).toLocaleDateString('fr-FR')}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <ChannelBadge channel={job.channel} />
                        <StatusBadge status={job.status} />
                        <RiskBadge level={job.risk_level} />
                        <div className="w-12 text-right">
                          <span className="text-sm font-medium text-slate-600">
                            {Math.round(job.confidence_score * 100)}%
                          </span>
                        </div>
                        <ChevronRight className="h-5 w-5 text-slate-400" />
                      </div>
                    </div>

                    {job.guest_comment && job.job_type === 'host_reply' && (
                      <div className="mt-3 pt-3 border-t border-slate-100">
                        <p className="text-sm text-slate-600 italic line-clamp-2">
                          "{job.guest_comment}"
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export const GuestReviewsPage = () => <ReviewJobsListPage jobType="guest_review" />;
export const HostRepliesPage = () => <ReviewJobsListPage jobType="host_reply" />;

export default ReviewJobsListPage;
